import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import Button, { ButtonProps } from '@components/Button';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';

export type SubmenuNavProps = ButtonProps & {
  name: string;
  icon?: (isOpen: boolean) => React.JSX.Element;
  image?: React.JSX.Element;
  isScrollStyle?: boolean;
  openMenuClasses?: string;
  openOnHover: boolean;
  submenuClasses?: string;
};

const SubmenuNav = ({
  className,
  children,
  icon,
  image,
  isScrollStyle,
  name,
  openMenuClasses,
  openOnHover,
  submenuClasses,
  ...rest
}: SubmenuNavProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);
  const { trackEvent } = useEventTracking();

  const openOnHoverAttributes = openOnHover
    ? {
        // need to delay hover effect after click for mobile to prevent click from hiding the menu
        onMouseEnter: () => setTimeout(() => setIsOpen(true), 0),
        onMouseLeave: () => setIsOpen(false),
      }
    : {};

  const handleNavClick = () => {
    setIsOpen((isOpen) => !isOpen);
    trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: name }));
  };

  useEffect(() => {
    const closeMenuOnClickAway = (event: MouseEvent) => {
      if (!navRef.current.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('click', closeMenuOnClickAway);
    return () => {
      document.removeEventListener('click', closeMenuOnClickAway);
    };
  }, [name]);

  return (
    <nav
      ref={navRef}
      id={`${name.toLowerCase()}-submenu-nav`}
      aria-label={name}
      className={clsx({ relative: isScrollStyle }, 'z-[1]')}
      {...openOnHoverAttributes}
    >
      <Button
        className={clsx(className, {
          [openMenuClasses]: isOpen,
        })}
        onClick={handleNavClick}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls={`${name.toLowerCase()}-submenu-nav-list`}
        {...rest}
      >
        {image}
        <span className={clsx({ 'sr-only': image })}>{name}</span>
        <span className="sr-only"> menu</span>
        {icon && icon(isOpen)}
      </Button>
      <div
        id={`${name.toLowerCase()}-submenu-nav-list`}
        className={clsx(submenuClasses, {
          hidden: !isOpen,
        })}
      >
        {children}
      </div>
    </nav>
  );
};

export default SubmenuNav;
